import {
  DeleteStoreItemsMutationMutation,
  GetStoreItemsDocument,
  StoreItemInput,
  useCreateStoreItemMutation,
  useDeleteStoreItemsMutationMutation as useDeleteStoreItemsMutation,
  useDuplicateStoreItemMutation,
  useGetStoreItemsQuery,
  useUpdateStoreItemMutation,
} from "@/graphql";
import useToast from "@/hooks/useToastNotification";
import { FetchResult } from "@apollo/client";
import { computed } from "vue";

const { toastError, toastSuccess } = useToast();

export function useItems(gameId: string, versionId: string) {
  const {
    result,
    loading: queryLoading,
    refetch: refetchItems,
  } = useGetStoreItemsQuery({ gameId, versionId }, { fetchPolicy: "cache-and-network" });
  const itemsToDisplay = computed(() => result.value?.getStoreItems || []);
  const nonConsumableItems = computed(() => itemsToDisplay.value.filter((item) => item?.type === "NON_CONSUMABLE"));
  const isWIP = computed(() => versionId === "wip");

  const { mutate: createStoreItem, loading: addItemLoading } = useCreateStoreItemMutation({});
  const { mutate: deleteStoreItems, loading: deleteStoreItemsLoading } = useDeleteStoreItemsMutation({});
  const { mutate: updateStoreItem, loading: updateItemLoading } = useUpdateStoreItemMutation({});
  const { mutate: duplicateStoreItem } = useDuplicateStoreItemMutation({});

  const addItem = async (input: StoreItemInput) => {
    try {
      if (!isWIP.value) {
        toastError({
          detail: "You cannot add items on non WIP version.",
          summary: "Error",
        });
        return;
      }
      await createStoreItem(
        { gameId, input, versionId },
        {
          update: (cache, { data }) => {
            cache.updateQuery({ query: GetStoreItemsDocument, variables: { gameId, versionId } }, (existingData) => {
              if (data?.createStoreItemMutation) {
                const newItem = data.createStoreItemMutation;
                const existingItems = existingData.getStoreItems;
                return {
                  getStoreItems: [...existingItems, newItem],
                };
              }
              return existingData;
            });
          },
        }
      );

      toastSuccess({
        detail: "Item created successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } catch (error) {
      console.log(error);
      toastError({
        detail: "Failed to create store item. Please try again.",
        summary: "Error",
      });
    }
  };

  const deleteItems = async (itemIds: string[]) => {
    try {
      if (!isWIP.value) {
        toastError({
          detail: "You cannot delete items on non WIP version.",
          life: 3 * 1000,
          summary: "Error",
        });
        return;
      }
      const response: FetchResult<DeleteStoreItemsMutationMutation> | null = await deleteStoreItems(
        { gameId, versionId, input: { storeItemIds: itemIds } },
        {
          update: (cache, deleteResult) => {
            const successfulIds = deleteResult.data?.deleteStoreItemsMutation?.successfulIds;
            if (successfulIds && successfulIds.length > 0) {
              for (const itemId of successfulIds) {
                cache.evict({ id: `StoreItem:${itemId}` });
              }
            }
          },
        }
      );
      if (response && response.data?.deleteStoreItemsMutation?.successfulIds === itemIds) {
        toastSuccess({
          detail: `Item${itemIds.length > 1 ? "s" : ""} deleted successfully.`,
          life: 3 * 1000,
          summary: "Success",
        });
      } else if (response && response.data?.deleteStoreItemsMutation?.failureReason === "StoreItemIsLinkedToReward") {
        toastError({
          detail: "Failed to delete store item because it is linked to a reward",
          summary: "Error",
        });
      }
    } catch (error) {
      toastError({
        detail: "Failed to delete store item. Please try again.",
        summary: "Error",
      });
    }
  };

  const updateSprite = async (itemId: string, sprite?: string) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot edit items on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    await updateStoreItem({ gameId, versionId, storeItemId: itemId, input: { sprite } });
  };

  const updateTags = async (itemId: string, updatedTagIds: string[], sprite?: string) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot update tags on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    await updateStoreItem({ gameId, versionId, storeItemId: itemId, input: { tags: updatedTagIds, sprite } });
  };

  const updateType = async (itemId: string, type: string, sprite?: string) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot update type on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    await updateStoreItem({ gameId, versionId, storeItemId: itemId, input: { type, sprite } });
  };

  const updateItem = async (itemId: string, updatedFields: Partial<StoreItemInput>) => {
    try {
      if (!isWIP.value) {
        toastError({
          detail: "You cannot update item on non WIP version.",
          life: 3 * 1000,
          summary: "Error",
        });
        return;
      }
      await updateStoreItem(
        { gameId, versionId, storeItemId: itemId, input: updatedFields },
        {
          update: (cache, { data }) => {
            cache.modify({
              id: `StoreItem:${itemId}`,
              fields: {
                name: () => data?.updateStoreItemMutation?.name,
                tags: () => data?.updateStoreItemMutation?.tags,
                sprite: () => data?.updateStoreItemMutation.sprite,
                technicalName: () => data?.updateStoreItemMutation?.technicalName,
                type: () => data?.updateStoreItemMutation?.type,
              },
            });
          },
        }
      );

      toastSuccess({
        detail: "Item updated successfully.",
        life: 3000,
        summary: "Success",
      });
    } catch (error) {
      toastError({
        detail: "An error occurred while updating the item. Please try again.",
        summary: "Error",
      });
    }
  };

  const duplicateItem = async (storeItemId: string, newName: string) => {
    const response = await duplicateStoreItem({ storeItemId, newName, gameId, versionId });

    if (response?.data?.duplicateStoreItemMutation?.success) {
      toastSuccess({
        detail: "Item duplicated successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } else {
      toastError({
        detail: response?.data?.duplicateStoreItemMutation?.details ?? "An error occurred while duplicating the item",
        summary: "Error",
      });
    }
  };

  return {
    itemsToDisplay,
    addItem,
    queryLoading,
    updateSprite,
    addItemLoading,
    deleteItems,
    deleteStoreItemsLoading,
    updateItem,
    updateItemLoading,
    updateTags,
    refetchItems,
    updateType,
    nonConsumableItems,
    duplicateItem,
  };
}
